import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StatusTimeline from '../components/StatusTimeline';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { API_ENDPOINTS } from '../config/api';

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useCustomerAuth();
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payingNow, setPayingNow] = useState(false);
  const [payError, setPayError] = useState('');
  const [confirming, setConfirming] = useState(false);

  // A delivered print order: the customer confirms it arrived (2026-09-28).
  const handleReceived = async () => {
    setConfirming(true);
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.orderReceived(orderId), { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.order) setOrder((o) => ({ ...o, ...data.order }));
    } finally {
      setConfirming(false);
    }
  };
  const justPaid = searchParams.get('paid') === '1';

  useEffect(() => {
    Promise.all([
      fetch(API_ENDPOINTS.customerProfile.orders, { headers: getAuthHeaders() }).then((r) => r.json()),
      fetch(API_ENDPOINTS.customerProfile.orderTimeline(orderId), { headers: getAuthHeaders() }).then((r) => r.json()),
    ])
      .then(([ordersData, timelineData]) => {
        const found = (ordersData.orders || []).find((o) => o.id === orderId);
        if (!found) throw new Error('Order not found');
        setOrder(found);
        setTimeline(timelineData.events || []);
      })
      .catch((err) => setError(err.message || 'Could not load this order'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handlePayNow = async () => {
    setPayError('');
    setPayingNow(true);
    try {
      const res = await fetch(API_ENDPOINTS.printOrders.checkout(orderId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/orders/${orderId}?paid=1`,
          cancelUrl: `${window.location.origin}/orders/${orderId}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setPayError(err.message || 'Could not start checkout');
      setPayingNow(false);
    }
  };

  return (
    <>
      <Header />
      <main className="section">
        <div className="container" style={{ maxWidth: 640 }}>
          <Link to="/account/orders" style={{ fontSize: '13px', color: 'var(--text-light)' }}>&larr; Back to orders</Link>

          {loading && <div className="loading" style={{ minHeight: '200px' }}><div className="loading-spinner" /></div>}
          {error && <div style={{ color: '#ef4444', marginTop: '16px' }}>{error}</div>}

          {order && (
            <>
              <div className="masthead" style={{ marginTop: '16px', marginBottom: '24px' }}>
                <span className="eyebrow">Order {order.orderNumber}</span>
                <h1>${order.total?.toFixed?.(2) ?? order.total}</h1>
                <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                {order.paymentStatus === 'paid' ? (
                  <p style={{ color: 'var(--primary-color)', fontWeight: 600 }}>✓ Paid</p>
                ) : justPaid ? (
                  <p style={{ color: 'var(--text-light)' }}>Payment received — updating your order…</p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handlePayNow}
                      disabled={payingNow}
                      className="btn btn-primary"
                      style={{ marginTop: '10px' }}
                    >
                      {payingNow ? 'Redirecting to secure checkout…' : `Pay $${order.total?.toFixed?.(2) ?? order.total} now`}
                    </button>
                    {payError && <div style={{ color: '#ef4444', marginTop: '8px', fontSize: '13px' }}>{payError}</div>}
                  </>
                )}
              </div>

              <StatusTimeline events={timeline} />

              {order.customerAccessToken && (
                <Link to={`/order/${order.id}?t=${order.customerAccessToken}`} className="btn btn-secondary" style={{ marginTop: '16px' }}>
                  Open the full order page - proof, tracking, report a problem, order again
                </Link>
              )}

              {order.printStatus === 'completed' && order.deliveryMode === 'ship' && (
                order.deliveredAt ? (
                  <p style={{ marginTop: '16px', color: 'var(--primary-color)', fontWeight: 600 }}>
                    📦 Received {new Date(order.deliveredAt).toLocaleDateString()} - thank you!
                  </p>
                ) : (
                  <button type="button" onClick={handleReceived} disabled={confirming} className="btn btn-primary" style={{ marginTop: '16px' }}>
                    {confirming ? 'Saving…' : '📦 I’ve received it'}
                  </button>
                )
              )}

              <div className="section-heading" style={{ marginTop: '32px', textAlign: 'left' }}>
                <h2 style={{ textAlign: 'left' }}>Items</h2>
              </div>
              {(order.items || []).map((item) => (
                <div key={item.id} className="feature-row">
                  <div>
                    <b style={{ display: 'block', fontWeight: 700, color: 'var(--secondary-color)' }}>{item.name}</b>
                    <span style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                      Qty {item.quantity} × ${item.price?.toFixed?.(2) ?? item.price}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
